import { useEffect, useRef, useState } from "react";
import type { ClickFixConfig, DesktopConfig } from "../../scenario/types";
import { Desktop } from "../Desktop";
import { WindowTitlebar } from "../WindowTitlebar";
import { audio } from "../../lib/audio";
import { activeTtsLang } from "../../lib/locale";
import type { MfitDone } from "../mfit/shared";

/**
 * The scammer's remote session on Alex's laptop. Nobody touches the mouse:
 * the cursor moves on its own, opens File Explorer, uploads the Finance files
 * to the scammer and exports the browser's saved passwords. Spoken lines are
 * prefetched on mount so the voice prerender records them.
 */
type Phase = "connecting" | "explorer" | "finance" | "selected" | "upload" | "chrome" | "exported" | "done";

const FILES = ["Supplier contracts Q4.xlsx", "Bank details - suppliers.xlsx", "Payment run 14 Oct.pdf", "NDA NorthBay - Lumen.docx", "Expenses October.xlsx"];
const LINE_1 = "Perfect, I can see your screen now. Just leave everything to me.";
const LINE_2 = "I'm just running a quick security scan for you. This only takes a minute.";

export function Takeover({ config, finish }: { config: ClickFixConfig; finish: (d: MfitDone) => void }) {
  const [phase, setPhase] = useState<Phase>("connecting");
  const [cursor, setCursor] = useState({ x: 52, y: 70 });
  const [clickFx, setClickFx] = useState(0);
  const [uploaded, setUploaded] = useState(0);
  const timers = useRef<number[]>([]);
  const voice = { voice: config.takeoverVoice ?? "echo", lang: activeTtsLang() };
  const tool = config.remoteTool ?? "QuickHelp Remote";
  const partner = config.remoteCode ?? "482 917";

  useEffect(() => {
    void audio.prefetchSpeech(LINE_1, voice);
    void audio.prefetchSpeech(LINE_2, voice);
    const at = (ms: number, fn: () => void) => timers.current.push(window.setTimeout(fn, ms));
    const click = () => {
      audio.click();
      setClickFx((n) => n + 1);
    };
    at(900, () => void audio.speak(LINE_1, voice));
    at(1700, () => setCursor({ x: 8, y: 15 }));
    at(2700, () => {
      click();
      audio.ui("appOpen");
      setPhase("explorer");
    });
    at(3500, () => setCursor({ x: 33, y: 36 }));
    at(4300, () => {
      click();
      setPhase("finance");
    });
    at(5100, () => setCursor({ x: 48, y: 42 }));
    at(5800, () => {
      click();
      setPhase("selected");
      void audio.speak(LINE_2, voice);
    });
    at(6900, () => setCursor({ x: 78, y: 70 }));
    at(7600, () => {
      click();
      setPhase("upload");
    });
    FILES.forEach((_, i) =>
      at(8300 + i * 650, () => {
        audio.dataBlip();
        setUploaded(i + 1);
      }),
    );
    const afterUpload = 8300 + FILES.length * 650 + 500;
    at(afterUpload, () => setCursor({ x: 8, y: 33 }));
    at(afterUpload + 800, () => {
      click();
      audio.ui("appOpen");
      setPhase("chrome");
    });
    at(afterUpload + 1700, () => setCursor({ x: 66, y: 48 }));
    at(afterUpload + 2500, () => {
      click();
      audio.dataBlip();
      setPhase("exported");
    });
    at(afterUpload + 4200, () => setPhase("done"));
    return () => {
      timers.current.forEach((t) => window.clearTimeout(t));
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deskConfig: DesktopConfig = {
    clock: config.clock,
    date: config.date,
    icons: [],
    taskbar: [
      { id: "browser", label: "Browser", icon: "chrome", opens: "browser" },
      { id: "files", label: "File Explorer", icon: "files", opens: "files" },
    ],
  };
  const explorerOpen = phase === "explorer" || phase === "finance" || phase === "selected" || phase === "upload";
  const chromeOpen = phase === "chrome" || phase === "exported";

  return (
    <Desktop config={deskConfig} className="cfx-desk cfx-takeover" onOpen={() => undefined}>
      <div className="cfx-rs-banner">
        <span className="cfx-rs-dot" />
        {tool}: partner <b>{partner}</b> is controlling this device
        <span className="cfx-rs-tag">{phase === "connecting" ? "connecting..." : "remote control active"}</span>
      </div>

      <div className="cfx-rs-icons" aria-hidden="true">
        <div className={`cfx-rs-icon${phase === "explorer" ? " is-sel" : ""}`}>
          <span className="cfx-rs-ico is-files" />
          File Explorer
        </div>
        <div className={`cfx-rs-icon${chromeOpen ? " is-sel" : ""}`}>
          <span className="cfx-rs-ico is-chrome" />
          Chrome
        </div>
      </div>

      {explorerOpen && (
        <div className="cfx-win cfx-rs-explorer">
          <WindowTitlebar title="File Explorer" />
          <div className="cfx-rs-ex-body">
            <aside>
              <span>Desktop</span>
              <span className={phase !== "explorer" ? "is-on" : ""}>Finance</span>
              <span>Documents</span>
              <span>Downloads</span>
            </aside>
            <section>
              {phase === "explorer" ? (
                <div className="cfx-rs-folder">
                  <span className="cfx-rs-ico is-folder" />
                  Finance
                </div>
              ) : (
                <ul className="cfx-rs-files">
                  {FILES.map((f, i) => (
                    <li key={f} className={phase === "selected" || phase === "upload" ? "is-sel" : ""}>
                      <span className="cfx-rs-ico is-file" />
                      {f}
                      {phase === "upload" && <em>{i < uploaded ? "Uploaded" : "Waiting"}</em>}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      )}

      {(phase === "selected" || phase === "upload") && (
        <div className="cfx-rs-transfer">
          <b>{tool} · File transfer</b>
          <span>To: partner {partner}</span>
          <div className="cfx-rs-bar">
            <i style={{ width: `${(uploaded / FILES.length) * 100}%` }} />
          </div>
          <small>{phase === "upload" ? `${uploaded} of ${FILES.length} files sent` : "Send selected files"}</small>
        </div>
      )}

      {chromeOpen && (
        <div className="cfx-win cfx-rs-chrome">
          <WindowTitlebar title="Chrome" />
          <div className="cfx-addr">
            <span className="cfx-addr-nav" aria-hidden="true">{"\u2039 \u203A \u21BB"}</span>
            <span className="cfx-addr-url">chrome://password-manager/settings</span>
          </div>
          <div className="cfx-rs-pw">
            <b>Password Manager</b>
            <span>148 saved passwords</span>
            <div className={`cfx-rs-export${phase === "exported" ? " is-done" : ""}`}>
              {phase === "exported" ? "Passwords exported to Downloads \u2192 uploaded" : "Export passwords"}
            </div>
          </div>
        </div>
      )}

      {phase !== "done" && (
        <div className="cfx-rs-cursor" style={{ left: `${cursor.x}%`, top: `${cursor.y}%` }} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22">
            <path d="M3 2l7 19 2.6-7.4L20 11z" fill="#fff" stroke="#111" strokeWidth="1.4" strokeLinejoin="round" />
          </svg>
          <span key={clickFx} className="cfx-rs-ripple" />
        </div>
      )}

      {phase === "done" && (
        <div className="cfx-rs-done">
          <b>The scammer is in.</b>
          <p>
            Nobody touched the mouse. In under a minute the 'technician' sent the Finance files to himself and exported
            148 saved passwords. Nothing was ever wrong with the laptop.
          </p>
          <button type="button" className="cfx-btn cfx-btn--primary cf-pulse-target" onClick={() => finish({ key: "done" })}>
            {config.continueLabel ?? "See what just happened"}
          </button>
        </div>
      )}
    </Desktop>
  );
}
